import { BodyPrimary, BodySecondary, H4 } from '@liveauctioneers/hammer-ui-core/text';
import { buildQueryUrl, getCategoryFacetLink } from '@/utils/urls';
import { getFilterActionByFacetId } from '@/pages/Search/KeywordSearch/utils/getFilterActionByFacetId';
import {
    getRawArchivedSearchFacets,
    getRawUpcomingSearchFacets,
} from '@/redux/modules/searchFacetAggregates.selectors';
import { isArchivedSearch, modifyParamsWithTaxonomySelected } from '@/utils/queryParams';
import { useAppSelector } from '@/redux/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import React, { ReactElement } from 'react';
import styled, { css, useTheme } from 'styled-components';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

export type TaxonomyNodeProps = {
    facetId: string;
    facets: any[];
    onSeeMore: (event: React.SyntheticEvent) => void;
    queryParams: any;
    renderAsParent: boolean;
    seeMore: boolean;
    showLandingPageLinks: boolean;
};

const TaxonomyNode = ({
    facetId,
    facets,
    onSeeMore,
    queryParams,
    renderAsParent,
    showLandingPageLinks,
}: TaxonomyNodeProps): ReactElement => {
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme();
    const isMobile = useIsNarrowerThan(tabletNarrowWidthNum + 1);

    const alphabetizeSubcategories = (categories) => {
        // sort categories alphabetically by many
        return categories.sort((a, b) => (a.name > b.name ? 1 : -1));
    };

    const { pathname } = useLocation();
    const navigate = useNavigate();

    const archivedSearch = isArchivedSearch(queryParams, pathname);
    const isArchivedData = useAppSelector(getRawArchivedSearchFacets);
    const isNotArchivedData = useAppSelector(getRawUpcomingSearchFacets);
    const rawFacets = archivedSearch ? isArchivedData : isNotArchivedData;

    const renderCategoryItem = React.useCallback(
        (categoryItem: any, queryParams: any, showLandingPageLinks: boolean) => {
            if (categoryItem.selected) {
                return null;
            }

            if (!categoryItem.landingPageUrl || !showLandingPageLinks) {
                const newParams = modifyParamsWithTaxonomySelected(queryParams, facetId, categoryItem.id, rawFacets);

                return (
                    <StyledLink
                        onClick={(e) => {
                            e.preventDefault();
                            navigate(buildQueryUrl(pathname, newParams), {
                                state: {
                                    filterAction: getFilterActionByFacetId({ facetId, isRemovingFilter: false }),
                                },
                            });
                        }}
                        route
                        to={buildQueryUrl(pathname, newParams)}
                    >
                        {isMobile ? (
                            <BodyPrimary color="secondary">{categoryItem.name}</BodyPrimary>
                        ) : (
                            <CategoryLinkTextDesktop
                                color="secondary"
                                data-testid="categoryItems"
                            >
                                {categoryItem.name}
                            </CategoryLinkTextDesktop>
                        )}
                    </StyledLink>
                );
            } else {
                return (
                    <StyledLandingPageLink
                        route
                        to={getCategoryFacetLink(categoryItem.landingPageUrl, queryParams)}
                    >
                        {isMobile ? (
                            <BodyPrimary color="secondary">{categoryItem.name}</BodyPrimary>
                        ) : (
                            <CategoryLinkTextDesktop color="secondary">{categoryItem.name}</CategoryLinkTextDesktop>
                        )}
                    </StyledLandingPageLink>
                );
            }
        },
        [facetId, isMobile, navigate, pathname, rawFacets]
    );

    const facetsDom = facets.map((facet, index) => {
        return (
            <StyledIndentation
                $renderAsParent={renderAsParent}
                key={`${facet.id} ${index}`}
            >
                {renderCategoryItem(facet, queryParams, showLandingPageLinks)}
                {!facet.categories ? null : (
                    <TaxonomyNode
                        facetId={facetId}
                        facets={alphabetizeSubcategories(facet.categories)}
                        onSeeMore={onSeeMore}
                        queryParams={queryParams}
                        renderAsParent={false}
                        seeMore={false}
                        showLandingPageLinks={showLandingPageLinks}
                    />
                )}
            </StyledIndentation>
        );
    });

    return <div>{facetsDom}</div>;
};

export default TaxonomyNode;

const CategoryLinkTextDesktop = styled(BodySecondary)`
    &:hover {
        color: ${({ theme }) => theme.colors.blue200};
        text-decoration: underline;
    }
`;

const StyledLink = styled(Link)`
    margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StyledLandingPageLink = styled(Link)`
    text-decoration: none;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StyledIndentation = styled(H4)<{ $renderAsParent: boolean }>`
    ${({ $renderAsParent }) =>
        $renderAsParent
            ? css`
                  && a {
                      text-decoration: none;
                  }

                  &:last-of-type > a {
                      margin-bottom: 0;
                  }
              `
            : css`
                  line-height: 20px;
                  display: inline-block;
                  width: 95.5%;

                  &:first-of-type > a {
                      margin-top: 0;
                  }

                  &:last-of-type > a {
                      margin-bottom: 0;
                  }
              `}
`;
